import { useEffect, useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Checkbox } from "@progress/kendo-react-inputs";
import { updateComponent } from "../../../../API/Display/Display";
import Scrollable from "../../../Shared/Edit/Scrollable";
import usePages, { useSelectedPage, useUpdatePageComponent } from "../../../../Hooks/Display/usePages";
import { useSelectedComponent } from "../../../../Hooks/Display/useComponents";
import { useAttributes, useComponentProperties } from "../../../../Hooks/Display/useComponentProperties";
import { useTranslation } from "react-i18next";

function SyncAttributeDialog() {

    const [{ pages }] = usePages();
    const [properties] = useComponentProperties();
    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();
    const updatePageComponent = useUpdatePageComponent(selectedPage.parentId, selectedPage._id);

    const [, attributeTableDispatcher] = useAttributes();

    const componentTypes = selectedComponent.componentType === "View" || selectedComponent.componentType === "Form" ? ["View", "Form"] : ["Table", "Cards", "Calendar", "List", "Kanban", "Summary"]

    let isMounted = true;

    const [loading, setLoading] = useState(true);
    const [components, setComponents] = useState([]);

    const { t } = useTranslation([
        "display/tables/attribute",
        "display/common",
    ], { useSuspense: false });

    useEffect(() => {
        function getData() {
            const components = [];
            for (const pageContainer of pages) {
                for (const page of pageContainer.items) {
                    for (const component of page.components) {
                        if (componentTypes.includes(component.componentType) && component.id !== selectedComponent.id) {
                            const checked = selectedComponent.componentsToSync && selectedComponent.componentsToSync.some(
                                ({ componentId, pageId }) => componentId === component.id && pageId === page._id
                            );
                            components.push({
                                componentName: component.componentName,
                                componentId: component.id,
                                componentType: component.componentType,
                                pageName: page.pageName,
                                pageId: page._id,
                                checked,
                            });
                        }
                    }
                }
            }

            if (isMounted) {
                setComponents(components);
                setLoading(false);
            }
        }

        setLoading(true);
        getData();
        attributeTableDispatcher.setSyncedComponentsChanged(false);

        return () => {
            isMounted = false;
        }
    }, []);

    function handleChange(event, componentId) {
        const checked = event.target.element.checked;
        setComponents(components => {
            return components.map(component => {
                if (component.componentId === componentId) {
                    return {
                        ...component,
                        checked
                    }
                }
                return component;
            })
        })
    }

    function handleSelectAll(event) {
        const checkAll = event.target.element.checked;
        setComponents(components => {
            return components.map(component => {
                return {
                    ...component,
                    checked: checkAll
                }
            })
        });
    }

    async function handleSave(event) {
        event.preventDefault();
        const componentsToSync = components.filter(component => component.checked).map(
            ({ componentId, pageId }) => ({ componentId, pageId })
        );
        await updateComponent(selectedPage._id, selectedComponent.id, { componentsToSync });
        updatePageComponent(selectedComponent.id, { ...properties, componentsToSync });
        handleClose();
        attributeTableDispatcher.setSyncedComponentsChanged(true);
    }

    function handleClose() {
        attributeTableDispatcher.toggleSyncAttributeDialog();
    }

    function getTranslatedComponentName(name) {
        const nameToTranslate = name.replace(/ /g, "_").toLowerCase();
        const translatedName = t(`components.${nameToTranslate}`, { ns: "display/common" });
        return translatedName === `components.${nameToTranslate}` ? name : translatedName;
    }

    return (
        <Dialog
            title={t("sync_attribute_dialog.title", { ns: "display/tables/attribute" })}
            height={"80%"}
            onClose={handleClose}
        >
            <Scrollable>
                {
                    !loading &&
                    <div>
                        <Checkbox
                            checked={components.every(component => component.checked)}
                            value={components.every(component => component.checked)}
                            onChange={handleSelectAll}
                            label={getTranslatedComponentName("select_all")}
                        />
                    </div>
                }
                {
                    !loading &&
                    components.map(component => {
                        return (
                            <div key={component.componentId}>
                                <Checkbox
                                    checked={component.checked}
                                    value={component.checked}
                                    onChange={(e) => handleChange(e, component.componentId)}
                                    label={getTranslatedComponentName(component.pageName)}
                                />
                            </div>
                        )
                    })}
            </Scrollable>
            <DialogActionsBar layout="end">
                <div className='mb-4 me-3 pe-2'>
                    <button className="btn btn-primary" onClick={handleSave}>
                        {t("sync_attribute_dialog.buttons.save", { ns: "display/tables/attribute" })}
                    </button>
                </div>
            </DialogActionsBar>
        </Dialog>
    )
}

export default SyncAttributeDialog;